/**
 * /admin/voice-check — Founder-only ElevenLabs voice preview.
 *
 * Type any line → hit "Speak" → the platform calls ElevenLabs with the current
 * ELEVENLABS_VOICE_ID and plays back the generated MP3 inline. Proves the
 * integration is live without waiting for a lesson trigger.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Volume2, ArrowLeft, ShieldCheck, AlertTriangle, PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const VoiceCheck = () => {
  const [text, setText] = useState('This is Coach B. Master the play. Know when to run it.');
  const [status, setStatus] = useState(null);      // { live, voice_id, model_id }
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusErr, setStatusErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastAudio, setLastAudio] = useState(null); // { url, provider, voice_id, size }

  useEffect(() => {
    (async () => {
      setStatusLoading(true);
      setStatusErr('');
      try {
        const r = await api.get('/school/voices', { headers: auth() });
        setStatus(r.data);
      } catch (e) {
        setStatusErr(e?.response?.data?.detail || 'Unable to load voice status. Please sign in as founder.');
      } finally { setStatusLoading(false); }
    })();
  }, []);

  const speak = async () => {
    const trimmed = text.trim();
    if (!trimmed) { toast.error('Type something for Coach B to say'); return; }
    if (trimmed.length > 500) { toast.error('500 character limit — trim it down'); return; }
    setBusy(true);
    setLastAudio(null);
    try {
      const r = await api.post('/school/voices/preview', { text: trimmed }, { headers: auth() });
      const b = r.data?.briefing || {};
      if (!b.audio_base64) {
        toast.error(`No audio returned — provider was "${b.provider || 'unknown'}". Check that ELEVENLABS_API_KEY is set.`);
        return;
      }
      const url = b.audio_url || `data:audio/mpeg;base64,${b.audio_base64}`;
      const size = Math.round((b.audio_base64.length * 0.75) / 1024);
      setLastAudio({ url, provider: b.provider, voice_id: b.voice_id, size, model: b.model_id });
      toast.success(`Voice generated · ${size} KB · ${b.provider}`);
      // auto-play
      setTimeout(() => {
        const audio = document.getElementById('voice-check-audio');
        audio?.play?.().catch(() => {});
      }, 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Voice preview failed');
    } finally { setBusy(false); }
  };

  const live = status?.live === true;
  const providerLabel = live ? 'ElevenLabs · Live' : 'Mock (browser TTS)';

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24 px-4" data-testid="voice-check">
      <div className="container mx-auto max-w-3xl">
        <Link to="/admin" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4" data-testid="voice-check-back">
          <ArrowLeft className="w-3 h-3" /> Admin Hub
        </Link>

        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500">Diagnostic · Coach B</div>
        <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2">
          Voice <span className="text-orange-500">Check</span>
        </h1>
        <p className="text-zinc-300 text-sm md:text-base mt-3 max-w-2xl leading-relaxed">
          Type any line up to 500 characters and hit <span className="text-white font-black">Speak</span>. The platform calls ElevenLabs with the current voice ID and plays back the generated MP3 — proof the integration is running end-to-end.
        </p>

        {/* Status card */}
        <div className="mt-6 border-2 border-zinc-900 bg-zinc-950 p-5" data-testid="voice-status">
          {statusLoading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading voice service…</div>
          ) : statusErr ? (
            <div className="flex items-start gap-3 text-sm text-red-300">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-red-400 font-black uppercase tracking-widest text-[10px]">Access Denied</div>
                <div className="mt-1">{statusErr}</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-[11px]">
              <div>
                <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Provider</div>
                <div className={`text-lg font-black mt-1 ${live ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="provider-label">{providerLabel}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Voice ID</div>
                <div className="font-mono text-[11px] mt-1 break-all">{status?.current_voice_id || '—'}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Model</div>
                <div className="font-mono text-[11px] mt-1">{status?.model_id || '—'}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Voices Available</div>
                <div className="font-black text-lg mt-1">{status?.voices?.length ?? 0}</div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="mt-6" data-testid="voice-input-card">
          <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-2 block">Line for Coach B ({text.length}/500)</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 500))}
            rows={4}
            className="w-full bg-black border-2 border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-sm"
            placeholder="Type any line up to 500 characters…"
            data-testid="voice-text-input"
          />
          <button onClick={speak} disabled={busy || !live}
            className="mt-3 w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-800 disabled:text-zinc-500 text-black py-4 text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2"
            data-testid="voice-speak-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
            {busy ? 'Generating…' : live ? 'Speak · Play Coach B' : 'Set ELEVENLABS_API_KEY to enable'}
          </button>
        </div>

        {/* Playback */}
        {lastAudio && (
          <div className="mt-6 border-2 border-emerald-500 bg-emerald-500/5 p-5" data-testid="voice-playback">
            <div className="flex items-center gap-2 text-emerald-400 mb-3">
              <ShieldCheck className="w-5 h-5" />
              <div className="text-[10px] font-black uppercase tracking-widest">Audio Generated · {lastAudio.size} KB</div>
            </div>
            <audio id="voice-check-audio" src={lastAudio.url} controls className="w-full" data-testid="voice-audio-player" />
            <div className="mt-3 text-[10px] text-zinc-400 grid grid-cols-2 gap-2">
              <div><span className="text-zinc-500 uppercase tracking-widest font-black">Provider</span> · {lastAudio.provider}</div>
              <div><span className="text-zinc-500 uppercase tracking-widest font-black">Voice</span> · {lastAudio.voice_id}</div>
              <div className="col-span-2"><span className="text-zinc-500 uppercase tracking-widest font-black">Model</span> · {lastAudio.model}</div>
            </div>
          </div>
        )}

        {/* Preset lines */}
        <div className="mt-8" data-testid="preset-lines">
          <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mb-3">Quick Test Lines</div>
          <div className="grid gap-2">
            {[
              'Master the play. Know when to run it.',
              "Outstanding, scholar. Your test-out scored 92 percent. That's elite-level execution.",
              'Welcome to SOLO — Self-Optimized Learning Opportunity. Time to build the athlete and the scholar.',
              "Coach B here. Today we're breaking down the pick-and-roll — but first, let's talk about geometry.",
            ].map((line) => (
              <button key={line} onClick={() => setText(line)}
                className="text-left border-2 border-zinc-900 bg-black hover:border-orange-500 transition-all p-3 text-[12px] text-zinc-300"
                data-testid={`preset-${line.slice(0, 20).replace(/\W/g, '-')}`}>
                <PlayCircle className="w-3 h-3 text-orange-500 inline mr-2" /> {line}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceCheck;
